import { useEffect, useRef, useState, type ReactNode } from 'react';
import { listModules } from '../harness/ModuleRegistry';
import { AtmosphericLayer } from '../modules/_shared/AtmosphericLayer';
import { BottomNav } from '../modules/_shared/BottomNav';
import { Sigil } from '../modules/_nav/Sigil';
import { IdeasView } from '../modules/_shared/IdeasView';
import { SeamlessFlowContext } from './SeamlessFlowContext';

/**
 * Seamless continuous-scroll showcase — master prompt §2.5.
 *
 * Architecture:
 *   - `SeamlessFlowContext.Provider value={true}` wraps the tree → per-module
 *     `AtmosphericLayer` instances become no-ops (see `_shared/AtmosphericLayer`).
 *   - A SINGLE global `AtmosphericLayer` is mounted at root with `position: fixed`
 *     so it never resets between sections — the canvas drifts under everything.
 *   - Each approved module renders inside a `<SeamlessSection>` wrapper which
 *     - mounts the module's content full-height (100dvh)
 *     - uses `content-visibility: auto` for off-screen perf (§7.10)
 *     - reveals itself via IntersectionObserver `data-entered="true"` once it
 *       crosses 30 % into the viewport — modules can hook on this for entry
 *       animations beyond what `.motion-*` utilities provide
 *   - Per-module section backgrounds (`bg-surface-main`) are stripped via CSS
 *     (`.seamless-section > section { background-color: transparent !important }`)
 *     so the global atmosphere shows through every screen, painting them as
 *     one continuous document.
 *
 * `/preview/:id` is unaffected — that route doesn't wrap in this context, so
 * each module renders its own scoped AtmosphericLayer in the iPhone frame.
 *
 * Module-specific atmosphere variants (Hero full / Pain calm / Перелом cyan-heavy)
 * are temporarily uniform under the global layer. A follow-up iteration can morph
 * the global layer's CSS vars based on which section is mid-viewport — that work
 * is independent of the seamless rework itself.
 */
export function Showcase() {
  const modules = listModules().filter((m) => m.status === 'approved');
  const [ideasOpen, setIdeasOpen] = useState(false);

  if (modules.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="seamless-root">
      {/* Global atmosphere — fixed, persistent across scroll.
          KEPT OUTSIDE the SeamlessFlowContext.Provider — otherwise this very
          AtmosphericLayer instance would itself be suppressed by the context
          flag, and the entire flow would render flat #1F1F1F with no spheres,
          no shafts, no drift gradient. (Bug surfaced 2026-06-04 when Wave 4
          first shipped; this is the one-line fix.) */}
      <div className="seamless-atmosphere" aria-hidden>
        <AtmosphericLayer spheres={3} shafts={2} intensity="full" />
      </div>

      {/* Provider wraps only the per-module track — child AtmosphericLayer
          instances inside any approved module become no-ops via the context. */}
      <SeamlessFlowContext.Provider value={true}>
        <div className="seamless-track">
          {modules.map(({ id, Component }, i) => (
            <SeamlessSection key={id} sectionId={id} index={i}>
              <Component />
            </SeamlessSection>
          ))}
        </div>
      </SeamlessFlowContext.Provider>

      {/* Global spinning ball — consistent top-right sigil, fixed across every
          screen (Igor round-3: «мячик всегда в одном месте, сверху справа,
          крутится»). Top-right counterpart to the per-module top-left wordmark.
          NOTE: 01-hero still carries its central hero-sigil — flagged for Igor;
          drop one if it reads as two balls there. */}
      <div
        className="pointer-events-none fixed right-sp-5 z-[60]"
        style={{ top: 'max(var(--safe-top, 0px), 1rem)' }}
        aria-hidden
      >
        <Sigil variant="nav" />
      </div>

      {/* ИДЕИ overlay — separate section toggled from the bottom-nav «Идеи»
          button. Sits above the flow (z-90), below the nav (z-100). */}
      <IdeasView open={ideasOpen} />

      {/* Global app-like bottom navigation — Igor 2026-06-04 golden iteration.
          Persistent across all sections; sits above the seamless-track and
          atmosphere via z-index 100 (see .bottom-nav). */}
      <BottomNav
        ideasOpen={ideasOpen}
        onIdeasToggle={() => setIdeasOpen((o) => !o)}
      />
    </div>
  );
}

interface SeamlessSectionProps {
  sectionId: string;
  index: number;
  children: ReactNode;
}

function SeamlessSection({ sectionId, index, children }: SeamlessSectionProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Mark entered when section is ~30 % visible — hook point for any module-side
    // entry animation that needs scroll-driven timing rather than mount-driven.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          el.dataset['entered'] = 'true';
        }
      },
      { rootMargin: '-30% 0px -30% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="seamless-section"
      data-section-id={sectionId}
      data-section-index={index}
    >
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <main
      className="min-h-dvh bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top), 1rem)',
        paddingBottom: 'max(var(--safe-bottom), 1rem)',
      }}
    >
      <div className="mx-auto flex max-w-[420px] flex-col gap-sp-6 px-sp-6 py-sp-9">
        <span className="font-ui text-caption uppercase tracking-[0.2em] text-text-muted">
          RAFL / digital
        </span>
        <h1 className="font-display text-display-xl leading-[1.05] tracking-[-0.02em]">
          Шоукейс собирается.
        </h1>
        <p className="font-ui text-body leading-[1.5] text-text-muted">
          Пока ни одного approved-модуля.
        </p>
      </div>
    </main>
  );
}
